import { CUSTOM_ELEMENTS_SCHEMA, Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { User, UserView } from '../../../models/user.model';
import { EMPTY, ReplaySubject, Subject, catchError, debounceTime, takeUntil, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { RouterModule } from '@angular/router';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { ToastrService } from '../../../services/toastr-service';
import { AppSupportService } from '../../../services/app-support.service';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import { UserSearchTextForm } from '../../../form-model/user-search-text.form.model';
import { UserForm } from '../../../form-model/user.form.model';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { UserFilterForm } from '../../../form-model/user-filter.form.model';
import { UserManagementService } from '../../../services/user-management.service';
import { USER_TABLE_CONFIG } from './user-config.component';
import { OverflowListComponent } from '../../../shared-components/overflow-list/overflow-list.component';
import { UserFilter } from '../../../models/user-filter.model';
import { AuthPolicies } from '../../../auth/auth-policies';

@Component({
  selector: 'app-user-grid',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxSkeletonLoaderModule, RouterModule, LoaderComponent, OverflowListComponent],
  templateUrl: './user-grid.component.html',
  styleUrl: './user-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserGridComponent implements OnInit, OnDestroy, OnChanges {
  data: UserView[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 20 }, (_, i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit = 20;
  sort: string | undefined;
  searchText?: string;
  getUsersApiCallStatus: APICallStatus = 'NOT_STARTED';
  openDeleteDialog = false;
  selectedRowData!: User;
  userEditForm: FormGroup<UserForm>;
  deleteApiCallStatus: APICallStatus = 'NOT_STARTED';

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    userSearchTextForm: FormGroup<UserSearchTextForm>;
    userFilterForm: FormGroup<UserFilterForm>;
  }>;

  @Input() activeTab?: number;

  activeUserTab = 0;

  columns: Array<TableColumn> = [];

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly cancelRequest$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly appSupportService: AppSupportService,
    private readonly appTextService: ApplicationtTextService,
    private readonly userManagementService: UserManagementService,
    public readonly authPolicy: AuthPolicies,
  ) {
    this.userEditForm = this.formBuilder.group({
      userName: ['', [Validators.maxLength(50), Validators.required]],
      firstName: ['', [Validators.maxLength(30), Validators.required]],
      lastName: ['', [Validators.maxLength(30), Validators.required]],
      emailAddress: ['', [Validators.maxLength(50), Validators.required, Validators.email]],
      companyName: ['', [Validators.maxLength(50), Validators.required]],
      locationCode: ['', [Validators.pattern(/^(?:[a-zA-Z0-9\s]+)?$/), Validators.maxLength(8), Validators.required]],
      role: [[]],
      region: [[]],
      shop: [''],
      isActive: [null],
      isExternal: [null],
    });
  }

  ngOnInit(): void {
    this.activeUserTab = this.activeTab ?? 0;
    this.sort = `createTimestamp:desc,updateTimestamp:desc`;
    this.setTableConfig();
    this.getUserList();
    this.subscribeToFormChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab'] && !changes['activeTab'].firstChange && changes['activeTab'].currentValue != null) {
      this.activeUserTab = changes['activeTab'].currentValue;
      this.setTableConfig();
      this.currentPage = 1;
      this.searchText = '';
      this.data = [];
      this.getUserList();
    }
  }
  private setTableConfig(): void {
    if (this.activeUserTab === 0) {
      this.columns = USER_TABLE_CONFIG.SHOP.columns;
    } else {
      this.columns = USER_TABLE_CONFIG.NON_SHOP.columns;
    }
  }

  private subscribeToFormChanges(): void {
    // ✅ SEARCH trigger
    this.filterFormGroup.controls.userSearchTextForm.controls.appliedUserSearchTextFilter.valueChanges
      .pipe(
        debounceTime(500), // ✅ debounce to avoid too many API calls while typing
        takeUntil(this.destroyed$),
      )
      .subscribe((value) => {
        if (value) {
          this.searchText = this.filterFormGroup.controls.userSearchTextForm.controls.searchText.value || '';
          this.currentPage = 1;
          this.getUserList();

          this.filterFormGroup.controls.userSearchTextForm.controls.appliedUserSearchTextFilter.setValue(false);
        }
      });

    // ✅ FILTER trigger (THIS FIXES YOUR ISSUE)
    this.filterFormGroup.controls.userFilterForm.controls.applyUserFilter.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.currentPage = 1;
          this.getUserList();

          this.filterFormGroup.controls.userFilterForm.controls.applyUserFilter.setValue(false);
        }
      });
  }

  onSortDirectionChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const sortColumn: string = customEvent.detail.column;
    const sortDirection: string = customEvent.detail.direction === 'ascending' ? 'asc' : 'desc';
    if (sortColumn !== 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getUserList();
    }
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getUserList();
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    if (changedPage !== this.currentPage) {
      this.currentPage = changedPage;
      this.getUserList();
    }
  }

  onApplyFilter(): void {
    if (this.filterFormGroup.controls.userSearchTextForm.valid) {
      this.searchText = this.filterFormGroup.controls.userSearchTextForm.controls.searchText.value || '';
      this.currentPage = 1;
      this.getUserList();
    }
  }
  private extractClusterNames(user: User): string[] {
    if (user.clusters && user.clusters.length > 0) {
      return user.clusters;
    }
    return user.regions?.flatMap((region) => region.clusters?.map((cluster) => cluster.name) || []) || [];
  }

  private cleanFilters(filters: UserFilter): Partial<UserFilter> {
    const cleaned: Partial<UserFilter> = {};

    (Object.keys(filters) as (keyof UserFilter)[]).forEach((key) => {
      if (key === 'applyUserFilter') return;

      const value = filters[key];

      if (Array.isArray(value)) {
        (cleaned as any)[key] = value.length ? value : null;
      } else if (value !== null && value !== '') {
        (cleaned as any)[key] = value;
      }
    });

    return cleaned;
  }

  getUserList(): void {
    this.cancelRequest$.next();
    this.getUsersApiCallStatus = 'IN_PROGRESS';
    this.data = [];
    const rawFilters = this.filterFormGroup.controls.userFilterForm.value;

    const filters = this.cleanFilters(rawFilters);

    this.userManagementService
      .getUserList(this.currentPage, this.pageLimit, this.sort, this.searchText, this.activeUserTab === 0, filters)
      .pipe(takeUntil(this.cancelRequest$), takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          this.data = (response.body || []).map(
            (user: User): UserView => ({
              ...user,

              shopCodes: [...new Set(user.maintenanceShops?.map((s) => s.maintenanceShopCode) || [])],
              rolesList: user.roles || [],
              regionsList: user.regions?.map((r) => r.name) || [],
              clustersList: this.extractClusterNames(user),
            }),
          );
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getUsersApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: () => {
          this.getUsersApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  get filteredData(): User[] {
    return this.data?.length ? this.data : (this.mockDataForLoadingAnimation as unknown as User[]);
  }

  showDeleteModalDialog(row: User): void {
    this.deleteApiCallStatus = 'NOT_STARTED';
    this.selectedRowData = row;
    this.openDeleteDialog = true;
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  onDeleteUserConfirmed(selectedRowData: User): void {
    this.deleteApiCallStatus = 'IN_PROGRESS';
    this.appSupportService
      .deleteUser(selectedRowData.userId)
      .pipe(
        catchError((err) => {
          this.deleteApiCallStatus = 'FAILED';
          this.toastrService.showToastr(err.error?.errorMessage || 'Failed to delete user', 'error');
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.deleteApiCallStatus = 'COMPLETED_SUCCESFULLY';
        timer(2000)
          .pipe(takeUntil(this.destroyed$))
          .subscribe(() => {
            this.openDeleteDialog = false;
            this.getUserList();
            this.toastrService.showToastr(this.appTextService.getUserDeletedSuccessfully(selectedRowData.username), 'success');
          });
      });
  }

  onDeleteUserConfirmationClosed(): void {
    this.openDeleteDialog = false;
    this.selectedRowData = {} as User;
    this.userEditForm.reset();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
